import React from 'react';
import BaseModule, { ModuleMetadata, NavigationItem, RouteItem, DashboardWidget, AnalyticsData, HelpContent } from './BaseModule';
import { Assessment, Timeline } from '@mui/icons-material';
import Analytics from './analytics/Analytics';
import MyAnalytics from './analytics/MyAnalytics';

export class AnalyticsModule extends BaseModule {
    constructor() {
        super();
        this.id = 'analytics';
        this.name = 'Analytics & Insights';
        this.description = 'Comprehensive analytics dashboard and performance metrics';
        this.version = '1.0.0';
        this.enabled = true;
    }

    /**
     * Get navigation items for the sidebar
     */
    getNavigationItems(user: any, userProfile: any): NavigationItem[] {
        const items: NavigationItem[] = [];

        // Global and Tenant Admins get full analytics
        if (this.isGlobalAdmin(user) || this.isTenantAdmin(user)) {
            items.push({
                name: 'Analytics',
                icon: <Assessment />,
                path: '/analytics',
                module: this.id
            });
        }

        // All users get My Analytics
        items.push({
            name: 'My Analytics',
            icon: <Timeline />,
            path: '/my-analytics',
            module: this.id
        });

        return items;
    }

    /**
     * Get React routes for the module
     */
    getRoutes(user: any, userProfile: any): RouteItem[] {
        const routes: RouteItem[] = [];

        if (this.isGlobalAdmin(user) || this.isTenantAdmin(user)) {
            routes.push({
                path: '/analytics',
                element: <Analytics />,
                module: this.id
            });
        }

        routes.push({
            path: '/my-analytics',
            element: <MyAnalytics />,
            module: this.id
        });

        return routes;
    }

    /**
     * Get dashboard widgets for the module
     */
    getDashboardWidgets(user: any, userProfile: any): DashboardWidget[] {
        return [
            {
                id: 'analytics-overview',
                title: 'Performance Overview',
                component: 'AnalyticsOverviewWidget',
                module: this.id,
                priority: 3
            }
        ];
    }

    /**
     * Get analytics data for the module
     */
    async getAnalyticsData(user: any, userProfile: any): Promise<AnalyticsData> {
        // This would typically fetch real data, but for the registry aggregration:
        return {
            totalPresentations: 0,
            completedEvaluations: 0,
            inProgressEvaluations: 0,
            failedEvaluations: 0,
            averageScore: 0,
            averageProcessingTime: 0,
            statusDistribution: {
                NEW: 0,
                IN_PROCESS: 0,
                VIDEO_ANALYSIS: 0,
                ANALYSE_ROLES: 0,
                EVALUATING_STRUCTURE: 0,
                EVALUATING_SOFT_SKILLS: 0,
                EVALUATING_KNOWLEDGE: 0,
                GENERATING_REPORT: 0,
                COMPLETE: 0,
                FAILED: 0
            }
        };
    }

    /**
     * Get help content for the module
     */
    getHelpContent(user: any, userProfile: any): HelpContent {
        return {
            '/analytics': {
                title: 'Analytics Dashboard',
                description: 'View comprehensive insights into presentation performance, including trends, score distributions, and consultant comparisons.',
                quickTips: [
                    'Use the date range selector to filter data',
                    'Switch tabs to view specific metrics (Trends, Distribution, etc.)',
                    'Export data to CSV for external analysis',
                    'Use advanced filters to drill down by consultant or category'
                ],
                faqs: [
                    {
                        question: 'How often is data updated?',
                        answer: 'Analytics charts update in near real-time as evaluations complete.'
                    },
                    {
                        question: 'Can I see individual user performance?',
                        answer: 'Yes, the "Consultants" tab provides a comparison of performance across different users.'
                    }
                ]
            },
            '/my-analytics': {
                title: 'My Analytics',
                description: 'Track your personal presentation history and improvement over time.',
                quickTips: [
                    'View your score progression chart to see improvement',
                    'Check your top categories to identify strengths'
                ]
            }
        };
    }

    /**
     * Get Firestore collections used by the module
     */
    getCollections(): string[] {
        return []; // Analytics mostly reads from other collections
    }

    /**
     * Get Firestore security rules for the module
     */
    getSecurityRules(): string {
        return ''; // Rules are typically on the data collections (presentations, etc.)
    }

    /**
     * Check if the module is available for the current user
     */
    isAvailable(user: any, userProfile: any): boolean {
        return this.enabled && !!user;
    }

    private isGlobalAdmin(user: any): boolean {
        // Simple check, in real app might use a more robust role check utility
        return user?.role === 'globalAdmin' || user?.claims?.role === 'globalAdmin';
    }

    private isTenantAdmin(user: any): boolean {
        return user?.role === 'tenantAdmin' || user?.claims?.role === 'tenantAdmin';
    }
}

export default AnalyticsModule;
